import { useRouter } from 'vue-router';
import { listPathFor } from '@/navigation/areas';
import { useEntityStore } from '@/stores/entityStore';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import type { IGroup, IRule } from '@/types/filterBuilderTypes';

/**
 * Point the search page at one entity with one rule, the way a person would by
 * picking the entity and adding the rule by hand. The rule is an ordinary rule
 * the person can see in the filter and delete.
 *
 * Writing the filter replaces the entity's current filter, and the search page
 * resets its paging whenever the filter changes.
 */
export function buildSingleRuleScope(rule: Omit<IRule, 'field'>): IGroup {
    return { id: 'root', scopeType: 'and', rules: [{ ...rule, field: null }] };
}

const LOCATION_ZONE_PLAYER_ENTITY_ID = 'LocationZonePlayer';

export function useShowInSearch() {
    const router = useRouter();
    const entityStore = useEntityStore();
    const persistentTableStore = usePersistentTableStore();

    function showRuleInSearch(entityId: string, rule: Omit<IRule, 'field'>): void {
        entityStore.setSelectedEntity(entityId);
        // The table store writes to whichever entity is selected, so this lands
        // on the entity just chosen.
        persistentTableStore.filter = { ruleScopes: buildSingleRuleScope(rule) };
    }

    /**
     * Opens the Players list with every player of these zones ("Location Zone Id
     * is one of ..."). The Sync pill sits on other pages too, such as Custom
     * playlists and a single player's page, so this goes to the Players list
     * first. Without that, a custom list page would show players under a
     * Custom playlists address, and a record page would show nothing.
     */
    async function showZonePlayersInSearch(zoneIds: number[]): Promise<void> {
        const navigationFailure = await router.push(listPathFor(LOCATION_ZONE_PLAYER_ENTITY_ID));
        if (navigationFailure) return;
        showRuleInSearch(LOCATION_ZONE_PLAYER_ENTITY_ID, {
            id: 'sync-zone-players',
            column: 'location_zone_id',
            criterion: 'contains_any',
            value: [...zoneIds],
        });
    }

    return { showZonePlayersInSearch };
}
